/**
 * Theme application engine.
 * Maps MonkeyType theme colors to ProvenLeap CSS variables,
 * applies them to the authenticated layout container, and persists selection in localStorage.
 * Public pages (landing, login) always use the default ProvenLeap theme from globals.css.
 */

import type { ThemeDefinition } from "./themes";
import { DEFAULT_THEME_ID } from "./themes";
import { hexToHslString, isLightColor, contrastForeground, mixHexToHslString } from "./color-utils";

const STORAGE_KEY = "provenleap-theme";
const STORAGE_VARS_KEY = "provenleap-theme-vars";

/** All CSS custom property names that the engine manages. */
const MANAGED_VARS = [
  "--background",
  "--foreground",
  "--card",
  "--card-foreground",
  "--popover",
  "--popover-foreground",
  "--primary",
  "--primary-foreground",
  "--secondary",
  "--secondary-foreground",
  "--muted",
  "--muted-foreground",
  "--accent",
  "--accent-foreground",
  "--destructive",
  "--destructive-foreground",
  "--border",
  "--input",
  "--ring",
  "--brand-primary",
  "--brand-primary-hover",
  "--brand-primary-light",
  "--app-surface",
  "--error",
  "--chart-1",
  "--neutral-50",
  "--neutral-100",
  "--neutral-200",
  "--neutral-300",
  "--neutral-400",
  "--neutral-500",
  "--neutral-600",
  "--neutral-700",
  "--neutral-800",
  "--neutral-900",
] as const;

/**
 * Maps MonkeyType's 10 colors to ProvenLeap's full CSS variable set.
 */
export function mapThemeToVariables(
  theme: ThemeDefinition
): Record<string, string> {
  const { bg, main, sub, subAlt, text, error } = theme.colors;
  const mainFg = contrastForeground(main);
  const bgHsl = hexToHslString(bg);
  const textHsl = hexToHslString(text);
  const mainHsl = hexToHslString(main);
  const subHsl = hexToHslString(sub);
  const subAltHsl = hexToHslString(subAlt);
  const errorHsl = hexToHslString(error);
  const light = isLightColor(bg);

  // Derive intermediate neutrals by interpolating between subAlt and text
  const subAltForBorder = subAltHsl;

  return {
    "--background": bgHsl,
    "--foreground": textHsl,
    "--card": bgHsl,
    "--card-foreground": textHsl,
    "--popover": bgHsl,
    "--popover-foreground": textHsl,
    "--primary": mainHsl,
    "--primary-foreground": hexToHslString(mainFg),
    "--secondary": subAltHsl,
    "--secondary-foreground": textHsl,
    "--muted": subAltHsl,
    "--muted-foreground": subHsl,
    "--accent": subAltHsl,
    "--accent-foreground": textHsl,
    "--destructive": errorHsl,
    "--destructive-foreground": hexToHslString(contrastForeground(error)),
    "--border": subAltForBorder,
    "--input": subHsl,
    "--ring": mainHsl,
    "--brand-primary": mainHsl,
    "--brand-primary-hover": mainHsl,
    "--brand-primary-light": light ? subAltHsl : bgHsl,
    // Recessed main-content surface. ProvenLeap keeps its signature translucent
    // blue wash (unchanged); every other theme uses its own `subAlt` nudged just
    // ~40% towards `bg` — a touch lighter than the alt background, but nowhere
    // near white, so the recessed pane stays distinct. Consumed as
    // `var(--app-surface)`.
    "--app-surface":
      theme.id === DEFAULT_THEME_ID
        ? "hsl(210 80% 95% / 0.3)"
        : `hsl(${mixHexToHslString(subAlt, bg, 0.4)})`,
    "--error": errorHsl,
    "--chart-1": mainHsl,
    "--neutral-50": light ? bgHsl : subAltHsl,
    "--neutral-100": subAltHsl,
    "--neutral-200": subAltHsl,
    "--neutral-300": subHsl,
    "--neutral-400": subHsl,
    "--neutral-500": subHsl,
    "--neutral-600": subHsl,
    "--neutral-700": textHsl,
    "--neutral-800": textHsl,
    "--neutral-900": textHsl,
  };
}

/**
 * Apply theme by setting CSS custom properties on a target element.
 *
 * Defaults to the document root (`<html>`) so the WHOLE document inherits the
 * theme — the app shell, cards, AND body-portaled dropdowns/dialogs (which live
 * outside the app subtree). The theme effect lives in the authenticated layout
 * and clears on unmount, so public pages (landing, login) fall back to the
 * default ProvenLeap theme. NOTE: `<html>` is never rendered by React, so the
 * `.dark` class set here can't be clobbered by a component re-render.
 */
export function applyTheme(theme: ThemeDefinition, target?: HTMLElement): void {
  const variables = mapThemeToVariables(theme);
  const el = target ?? document.documentElement;
  if (!el) return;

  for (const [prop, value] of Object.entries(variables)) {
    el.style.setProperty(prop, value);
  }

  // Toggle .dark class so Tailwind dark: variants work across all themes
  el.classList.toggle("dark", theme.isDark);

  localStorage.setItem(STORAGE_KEY, theme.id);
  localStorage.setItem(STORAGE_VARS_KEY, JSON.stringify(variables));
}

/**
 * Remove all theme overrides from the target element, reverting it to the default
 * ProvenLeap theme defined in globals.css `:root`.
 *
 * Intentionally does NOT reset the persisted theme id: the user's selection must
 * survive logout / public-page navigation and re-apply on the next login. Only
 * the live DOM overrides are removed.
 */
export function clearTheme(target?: HTMLElement): void {
  const el = target ?? document.documentElement;
  if (!el) return;

  for (const prop of MANAGED_VARS) {
    el.style.removeProperty(prop);
  }

  el.classList.remove("dark");
}

/**
 * Get the persisted theme ID from localStorage.
 */
export function getPersistedThemeId(): string {
  return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME_ID;
}
